import { useState } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

/**
 * Searchable product picker built on Popover + Command (cmdk).
 * Props:
 *   products    - array of { id, name, code, model, category }
 *   value       - selected product id
 *   onChange    - (productId) => void
 *   testId      - optional data-testid for the trigger button
 *   placeholder - trigger placeholder when empty
 */
export default function ProductPicker({
  products = [],
  value,
  onChange,
  testId,
  placeholder = "Pilih barang",
}) {
  const [open, setOpen] = useState(false);
  const selected = products.find((p) => p.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className="h-9 w-full justify-between font-normal text-left"
        >
          <span className={cn("truncate", !selected && "text-zinc-500")}>
            {selected ? (
              <>
                {selected.name}
                {selected.model && (
                  <span className="ml-2 text-xs font-mono text-zinc-600">
                    [{selected.model}]
                  </span>
                )}
                <span className="text-zinc-400 text-xs font-mono ml-2">
                  ({selected.code})
                </span>
              </>
            ) : (
              placeholder
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[400px] p-0"
        align="start"
        onOpenAutoFocus={(e) => {
          // Let Command's internal input take focus naturally.
        }}
      >
        <Command
          filter={(itemValue, search) => {
            // itemValue is the string we set on CommandItem's `value` prop.
            const s = search.toLowerCase();
            return itemValue.toLowerCase().includes(s) ? 1 : 0;
          }}
        >
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <CommandInput
              placeholder="Cari model, kode, nama, brand, atau kategori…"
              className="h-9 border-0 focus:ring-0 focus:outline-none"
              data-testid={testId ? `${testId}-search` : undefined}
            />
          </div>
          <CommandList className="max-h-72">
            <CommandEmpty>Barang tidak ditemukan.</CommandEmpty>
            <CommandGroup>
              {products.map((p) => {
                const searchable = [p.name, p.code, p.model, p.brand, p.category]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <CommandItem
                    key={p.id}
                    value={searchable}
                    onSelect={() => {
                      onChange?.(p.id);
                      setOpen(false);
                    }}
                    data-testid={`product-option-${p.code}`}
                    className="flex items-start gap-2"
                  >
                    <Check
                      className={cn(
                        "mt-0.5 h-3.5 w-3.5 shrink-0",
                        value === p.id ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {p.name}
                        {p.model && (
                          <span className="ml-2 text-xs font-mono text-emerald-700">
                            [{p.model}]
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-zinc-500 font-mono truncate">
                        {p.code}
                        {p.brand ? ` · ${p.brand}` : ""}
                        {p.category ? ` · ${p.category}` : ""}
                      </div>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
